import { useState } from 'react'
import { Card, CardHeader } from './Card.jsx'
import {
  downloadOrgQuarterlyReport,
  downloadNationalQuarterlyReport,
} from '../api/reports.js'

/**
 * Quarter picker + "Download quarterly report" button.
 *
 * scope="national" → platform admin national report.
 * scope="org"      → per-org report (requires orgId).
 *
 * Streams a PDF from the server and saves it via an object URL (same
 * pattern as the Value & Billing statement download).
 */
export default function QuarterlyReportButton({ scope = 'national', orgId = null }) {
  const now = new Date()
  const cy = now.getFullYear()
  const cq = Math.floor(now.getMonth() / 3) + 1

  const [year, setYear] = useState(cy)
  const [quarter, setQuarter] = useState(cq)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  const years = [cy, cy - 1, cy - 2]

  async function download() {
    setBusy(true)
    setErr('')
    try {
      const blob =
        scope === 'org'
          ? await downloadOrgQuarterlyReport(orgId, year, quarter)
          : await downloadNationalQuarterlyReport(year, quarter)
      const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }))
      const a = document.createElement('a')
      a.href = url
      a.download = `${scope === 'org' ? `org${orgId}` : 'national'}_quarterly_report_${year}_Q${quarter}.pdf`
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    } catch {
      setErr('Could not generate the report. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card>
      <CardHeader
        title="Quarterly health report"
        subtitle={
          scope === 'org'
            ? 'Download a board-ready PDF of this organisation’s quarterly health summary.'
            : 'Download a board-ready PDF of the national quarterly health summary.'
        }
        right={
          <span className="text-[10px] uppercase tracking-wide border rounded-full px-2 py-0.5 bg-wantok-50 text-wantok-700 border-wantok-200">
            PDF
          </span>
        }
      />
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-xs text-slate-500">
          Year
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="mt-1 block border rounded px-2 py-1 text-sm bg-white"
          >
            {years.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </label>
        <label className="text-xs text-slate-500">
          Quarter
          <select
            value={quarter}
            onChange={(e) => setQuarter(Number(e.target.value))}
            className="mt-1 block border rounded px-2 py-1 text-sm bg-white"
          >
            <option value={1}>Q1 (Jan–Mar)</option>
            <option value={2}>Q2 (Apr–Jun)</option>
            <option value={3}>Q3 (Jul–Sep)</option>
            <option value={4}>Q4 (Oct–Dec)</option>
          </select>
        </label>
        <button
          type="button"
          onClick={download}
          disabled={busy}
          className="px-4 py-2 rounded-md bg-wantok-700 text-white text-sm font-semibold hover:bg-wantok-800 disabled:opacity-50"
        >
          {busy ? 'Generating…' : '⬇ Download PDF'}
        </button>
        {err && <span className="text-xs text-rose-700">{err}</span>}
      </div>
    </Card>
  )
}
